import React, { createContext, useMemo, useEffect } from "react";
import { useParams } from "react-router";
import { transformFilterData } from "helpers/utility/utilities";
import { useGetUserConfigApiV1UserConfigGet } from "vl-api/vlComponents";
import { TransformedFeatureConfig, UserConfigApiResponse } from "types";

// Get workspace type from API response
export type UserWorkspace = NonNullable<
  UserConfigApiResponse["workspaces"]
>[number];

type UserPermissions = {
  can_view: boolean;
  can_edit: boolean;
  can_delete: boolean;
  can_manage_access: boolean;
  can_share: boolean;
  can_enrich: boolean;
  can_add_media: boolean;
  can_reindex: boolean;
  can_explore: boolean;
  can_export: boolean;
  can_search: boolean;
  can_create_snapshot: boolean;
  can_send_to_training: boolean;
  can_restore_snapshot: boolean;
  can_clone_snapshot: boolean;
  can_use_flywheel: boolean;
};

export type UserPermission = keyof UserPermissions;

export interface UserConfigContextType {
  config: TransformedFeatureConfig[];
  workspaces: UserWorkspace[];
  isLoading: boolean;
  error: boolean;
  refetch: () => void;
  permissions: UserPermissions;
}

export const UserConfigContext = createContext<
  UserConfigContextType | undefined
>(undefined);

// Global refetch trigger for calling from Redux actions
let globalUserConfigRefetch: (() => void) | null = null;

export const triggerUserConfigRefetch = () => {
  if (globalUserConfigRefetch) {
    globalUserConfigRefetch();
  }
};

export const UserConfigProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { datasetId, viewsDatasetId, enrichDatasetId, newDatasetId } =
    useParams();

  const actualDatasetId =
    datasetId || viewsDatasetId || enrichDatasetId || newDatasetId;

  const { data, isLoading, isError, refetch } =
    useGetUserConfigApiV1UserConfigGet({
      queryParams: actualDatasetId
        ? { dataset_id: actualDatasetId }
        : undefined,
    });

  // Register the refetch function globally so Redux actions can trigger it
  useEffect(() => {
    globalUserConfigRefetch = refetch;
    return () => {
      globalUserConfigRefetch = null;
    };
  }, [refetch]);

  const config = useMemo(() => {
    // Cast needed because the backend endpoint lacks a response_model annotation,
    // causing codegen to generate void. The actual response is UserConfigApiResponse.
    const response = data as unknown as UserConfigApiResponse | undefined;
    return transformFilterData(response?.features || []);
  }, [data]);

  const workspaces = useMemo(() => {
    // Cast needed because the backend endpoint lacks proper typing
    const response = data as any;
    return (response?.workspaces || []) as UserWorkspace[];
  }, [data]);

  const permissions = ((data as any)?.permissions || {}) as UserPermissions;

  return (
    <UserConfigContext.Provider
      value={{
        permissions,
        config,
        workspaces,
        isLoading,
        error: isError,
        refetch,
      }}
    >
      {children}
    </UserConfigContext.Provider>
  );
};
